import { create } from 'zustand';
import { getTenantConfig, type TenantId, type TenantConfig } from './tenantConfig';

// Détection du tenant au chargement du module.
//
// Ordre de priorité :
//   1. Query param URL (?tslh=1) → sessionStorage
//   2. sessionStorage persisté (survit à un refresh à l'intérieur du même onglet)
//   3. Fallback "default" (DynastyPool grand public)
//
// On persiste en sessionStorage (et non localStorage) pour rester cohérent
// avec embedStore et éviter qu'un utilisateur qui ferme le tab reste
// "coincé" dans un tenant après réouverture de dynastypool.net.

const TENANT_SESSION_KEY = 'dp_tenant';

function readSessionTenant(): TenantId | null {
  try {
    const raw = sessionStorage.getItem(TENANT_SESSION_KEY);
    if (raw === 'arsenal' || raw === 'tslh' || raw === 'default') return raw;
    return null;
  } catch {
    return null;
  }
}

function writeSessionTenant(id: TenantId): void {
  try {
    sessionStorage.setItem(TENANT_SESSION_KEY, id);
  } catch {
    /* ignore */
  }
}

function detectTenant(): TenantId {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tslh') === '1' || params.get('tslh') === 'true') {
      writeSessionTenant('tslh');
      return 'tslh';
    }
    // Arsenal reste piloté par sa propre logique (VITE_ARSENAL_MODE / OIDC).
    // On ne détecte pas Arsenal ici : le tenantStore est "additif" et sert
    // principalement à isoler TSLH sans toucher au flux Arsenal existant.
    const stored = readSessionTenant();
    if (stored) return stored;
    return 'default';
  } catch {
    return 'default';
  }
}

const _tenantId = detectTenant();

// Appliquer l'attribut data-tenant sur <html> pour scoper le branding CSS.
// Runs once at module load, mirror du pattern `embed-mode` dans embedStore.ts.
if (typeof document !== 'undefined') {
  const config = getTenantConfig(_tenantId);
  if (config.brandingClass) {
    document.documentElement.setAttribute('data-tenant', config.brandingClass);
  }
}

interface TenantState {
  tenantId: TenantId;
  config: TenantConfig;
}

export const useTenantStore = create<TenantState>(() => ({
  tenantId: _tenantId,
  config: getTenantConfig(_tenantId),
}));

/** Helpers synchrones utilisables hors composants React. */
export function getCurrentTenantId(): TenantId {
  return _tenantId;
}

export function getCurrentTenantConfig(): TenantConfig {
  return getTenantConfig(_tenantId);
}

/** Convenience helper : true si on est dans le mode TSLH. */
export function isTslhTenant(): boolean {
  return _tenantId === 'tslh';
}

/**
 * Propage le paramètre `?tslh=1` sur un path interne, comme `getEmbedAwarePath`
 * le fait pour `?embed=1`. Utile pour toute navigation programmatique afin de
 * garder le tenant sticky à travers les liens et redirections.
 */
export function getTenantAwarePath(path: string): string {
  if (_tenantId !== 'tslh') return path;
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('tslh', '1');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path.includes('?') ? `${path}&tslh=1` : `${path}?tslh=1`;
  }
}
